import { createSelector } from 'reselect';

import { alignLeftCellStyle, alignRightCellStyle } from '@common/cellStyles';
import { valueGetter, isCellEditable } from '../gridReport/columns/helpers';
import { gridNameCellDefinition, gridNumeralCellDefinition } from '../gridReport/columns/baseDefinitions';

const NON_EDITABLE_ROWS = [{
  key: 'DESCRIPTION',
  value: 'Total',
}];

const findFormOptions = (columnName, editableColumns = []) => (editableColumns && editableColumns.length)
  ? editableColumns.find(({ key }) => key === columnName)
  : null;

const findAccountId = account => {
  const result = /(\w+) -/.exec(account);

  if (!result) {
    return null;
  }

  return {
    label: account,
    value: result[1],
  };
};

const findEditableData = (columnName, editableColumns) => params => {
  const { DESCRIPTION: description } = params.data;

  return {
    formOptions: {
      ...findFormOptions(columnName, editableColumns),
      toAccount: findAccountId(description),
      allowFbosFlag: true,
    },
  };
};

const accountColumnDefinition = {
  headerName: 'Account',
  valueGetter: valueGetter('DESCRIPTION'),
  ...gridNameCellDefinition,
  ...alignLeftCellStyle,
  pinned: 'left',
};

const footnoteColumnDefinition = {
  headerName: 'Footnote',
  valueGetter: valueGetter('FOOTNOTE'),
  ...gridNameCellDefinition,
  ...alignLeftCellStyle,
  width: 75,
  minWidth: 75,
  pinned: 'left',
};

const deferredGroupColumnDefinition = {
  headerName: 'Deferred Grouping',
  valueGetter: valueGetter('DEFERRED_GROUP'),
  ...gridNameCellDefinition,
  ...alignLeftCellStyle,
  width: 170,
  minWidth: 170,
  pinned: 'left',
};

const footnoteDescColumnDefinition = {
  headerName: 'Footnote',
  valueGetter: valueGetter('DESCRIPTION'),
  ...gridNameCellDefinition,
  ...alignLeftCellStyle,
  width: 75,
  minWidth: 75,
  pinned: 'left',
};

const deferredGroupDescColumnDefinition = {
  headerName: 'Deferred Grouping',
  valueGetter: valueGetter('DEFERRED_GROUP'),
  ...gridNameCellDefinition,
  ...alignLeftCellStyle,
  width: 170,
  minWidth: 170,
  pinned: 'left',
};


const getBaseColumnDefinitions = editableColumns => [
  {
    headerName: 'Beginning Balance',
    valueGetter: valueGetter('BEGIN_BALANCE'),
    editable: isCellEditable('BEGIN_BALANCE', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('BEGIN_BALANCE', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Rate Change',
    valueGetter: valueGetter('DEF_RATE-CHG', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_RATE-CHG', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_RATE-CHG', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Return to Accrual (Def Only)',
    valueGetter: valueGetter('DEF_RTA', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_RTA', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_RTA', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Audit True up (Def Only)',
    valueGetter: valueGetter('DEF_AUDIT-TRUE-UP', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_AUDIT-TRUE-UP', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_AUDIT-TRUE-UP', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'DTA/(DTL) Adjustment',
    valueGetter: valueGetter('DEF_DT-ADJ', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_DT-ADJ', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_DT-ADJ', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Post Return Adjustment',
    valueGetter: valueGetter('DEF_PRA', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_PRA', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_PRA', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Balance Sheet Other (BS Only)',
    valueGetter: valueGetter('DEF_BS-OTH', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_BS-OTH', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_BS-OTH', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },

  {
    headerName: 'Adjusted Beginning Balance',
    valueGetter: valueGetter('ADJ_BEGIN_BALANCE', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('ADJ_BEGIN_BALANCE', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('ADJ_BEGIN_BALANCE', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'CY Activity (Activity)',
    valueGetter: valueGetter('DEF_PROV', { affectedTotals: ['END_BALANCE'] }),
    editable: false,
    cellEditorParams: findEditableData('DEF_PROV', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Provision (Def Only)',
    valueGetter: valueGetter('DEF_OTH-PROV', { affectedTotals: ['END_BALANCE', 'ADJ_BEGIN_BALANCE'] }),
    editable: isCellEditable('DEF_OTH-PROV', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_OTH-PROV', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Reclass (Def Only)',
    valueGetter: valueGetter('DEF_RECLASS', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('DEF_RECLASS', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_RECLASS', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Equity (BS Only)',
    valueGetter: valueGetter('DEF_EQUITY', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('DEF_EQUITY', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_EQUITY', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Goodwill (BS Only)',
    valueGetter: valueGetter('DEF_GOODWILL', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('DEF_GOODWILL', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_GOODWILL', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Other Comprehensive Income (BS Only)',
    valueGetter: valueGetter('DEF_OCI', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('DEF_OCI', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('DEF_OCI', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
    minWidth: 115,
  },
  {
    headerName: 'FX',
    valueGetter: valueGetter('FX', { affectedTotals: ['END_BALANCE'] }),
    editable: isCellEditable('FX', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('FX', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
  {
    headerName: 'Ending Balance',
    valueGetter: valueGetter('END_BALANCE'),
    editable: isCellEditable('END_BALANCE', editableColumns, NON_EDITABLE_ROWS),
    cellEditorParams: findEditableData('END_BALANCE', editableColumns),
    ...gridNumeralCellDefinition,
    ...alignRightCellStyle,
  },
];

// isClientLocked is not stored in the redux state, but is passed via mapStateToProps
const isClientLockedSelector = ({ props }) => props.isClientLocked;

export default (
  aggregationValueSelector,
  reportDataSelector,
  isAllowedToMakeDirectEntriesSelector
) => createSelector(
  [
    aggregationValueSelector,
    reportDataSelector,
    isAllowedToMakeDirectEntriesSelector,
    isClientLockedSelector,
  ],
  (aggregationValue, reportData, isAllowedToMakeDirectEntries, isClientLocked) => {
    const editableColumns = (isAllowedToMakeDirectEntries && reportData && !isClientLocked)
      ? reportData.editableColumns
      : [];
    const aggreationColumns = {
      account: [accountColumnDefinition,
        deferredGroupColumnDefinition,
        footnoteColumnDefinition,
        ...getBaseColumnDefinitions(editableColumns)],
      deferredGroup: [deferredGroupDescColumnDefinition, ...getBaseColumnDefinitions(editableColumns)],
      footnote: [footnoteDescColumnDefinition, ...getBaseColumnDefinitions(editableColumns)],
    };
    return aggreationColumns[aggregationValue] || [];
  }
);
